import { useEffect, useState } from "react";
import { Download, Share, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

// Detect if we're already running as an installed PWA
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator?.standalone === true;
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent || "");
}

const DISMISS_KEY = "operon.pwa.installDismissedAt";
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function wasDismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ago = Date.now() - Number(v);
    return ago < DISMISS_COOLDOWN_MS;
  } catch { return false; }
}

/**
 * InstallPrompt — PWA install affordance.
 *  - Android/Chrome: uses the native `beforeinstallprompt` event and shows a polished bottom sheet.
 *  - iOS/Safari: shows an instructional sheet pointing to the Share → Add to Home Screen flow.
 *  - Auto-hides once installed, or 7 days after dismissal.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt. Show the manual-instruction sheet
    // after a short delay so it doesn't compete with login.
    let iosTimer;
    if (isIOS() && !isStandalone() && !wasDismissedRecently()) {
      iosTimer = setTimeout(() => setShowIOS(true), 3500);
    }

    // Hide once installed
    const onInstalled = () => {
      setShowAndroid(false);
      setShowIOS(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShowAndroid(false);
    setShowIOS(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setShowAndroid(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    }
    setDeferred(null);
  };

  if (!showAndroid && !showIOS) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Operon"
      data-testid="pwa-install-prompt"
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:bottom-6 md:right-6 md:max-w-sm z-[200] rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-4 animate-in slide-in-from-bottom-4 fade-in"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="absolute top-2 right-2 p-1.5 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-semibold">Install Operon</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {showIOS
              ? "Add to your home screen for full-screen access and quick launch."
              : "Install the Operon app for faster launch and a focused experience."}
          </div>
        </div>
      </div>

      {showAndroid && (
        <div className="flex gap-2 mt-3">
          <Button variant="outline" size="sm" className="flex-1" onClick={dismiss} data-testid="pwa-install-later">
            Not now
          </Button>
          <Button size="sm" className="flex-1" onClick={install} data-testid="pwa-install-accept">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Install
          </Button>
        </div>
      )}

      {showIOS && (
        <div className="mt-3 text-xs text-muted-foreground space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-foreground text-[10px] font-bold">1</span>
            <span>Tap the <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> <b>Share</b> button in Safari.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-foreground text-[10px] font-bold">2</span>
            <span>Scroll down and tap <b>Add to Home Screen</b>.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-foreground text-[10px] font-bold">3</span>
            <span>Tap <b>Add</b> — Operon will launch like a native app.</span>
          </div>
          <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={dismiss} data-testid="pwa-install-later">Got it</Button>
        </div>
      )}
    </div>
  );
}

/**
 * Kick off the service-worker registration. Safe to call multiple times.
 * Must be called outside of React (so it runs even if the tree crashes).
 */
export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register in production-ish environments. The preview environment
  // is served over HTTPS so SW registration works.
  if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // When a new SW is found, activate it immediately.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              // Fresh version ready — prompt the SW to activate.
              reg.waiting?.postMessage("SKIP_WAITING");
            }
          });
        });
      })
      .catch(() => { /* silently ignore */ });
  });
}
